import { ProblemSchema } from '@socialfy/api-contract';
import { problemText } from '@/i18n/messages';

/** Wartezeit aus dem Kopf `Retry-After` in Sekunden (nur die Zahlenform, wie der Vertrag sie beschreibt). */
export function parseRetryAfter(raw: string | null): number | null {
  return raw !== null && /^\d{1,7}$/.test(raw) ? Number(raw) : null;
}

type ProblemFields = {
  code: string;
  status: number;
  requestId: string;
  reason?: string | undefined;
  detail?: string | undefined;
  fieldErrors?: Readonly<Record<string, readonly string[]>> | undefined;
  retryAfterSeconds?: number | null;
};

/**
 * Fehlerantwort der API (RFC 9457) als Ausnahme. Die Nachricht ist der Klartext aus dem Wörterbuch, nie
 * `detail` oder ein Stacktrace; `requestId` erscheint klein unter der Fehlerkarte.
 */
export class ApiProblem extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string;
  readonly reason: string | undefined;
  readonly detail: string | undefined;
  readonly fieldErrors: Readonly<Record<string, readonly string[]>> | undefined;
  readonly retryAfterSeconds: number | null;

  constructor(fields: ProblemFields) {
    super(problemText(fields.code, { reason: fields.reason }));
    this.name = 'ApiProblem';
    this.code = fields.code;
    this.status = fields.status;
    this.requestId = fields.requestId;
    this.reason = fields.reason;
    this.detail = fields.detail;
    this.fieldErrors = fields.fieldErrors;
    this.retryAfterSeconds = fields.retryAfterSeconds ?? null;
  }

  /** Eine Tages- oder Wochengrenze ist erreicht (V12-01); wird nie automatisch wiederholt. */
  get isLimit(): boolean {
    return this.code === 'rate_limited' && this.reason === 'limit_reached';
  }

  /** Die Schutzmarke gegen Fremdaufrufe wurde abgelehnt (W1-22 `requireCsrf`). */
  get isCsrf(): boolean {
    return this.code === 'forbidden' && this.reason === 'csrf';
  }
}

/** Die Antwort passt nicht zum Vertrag (Schema, Status, Format): ein Fehler der Anwendung, nicht des Nutzers. */
export class ApiContractError extends Error {
  readonly operationId: string;
  readonly issues: readonly string[];

  constructor(operationId: string, issues: readonly string[]) {
    super(`Antwort von ${operationId} entspricht nicht dem Vertrag: ${issues.join('; ')}`);
    this.name = 'ApiContractError';
    this.operationId = operationId;
    this.issues = issues;
  }
}

/** Keine Verbindung zum Server (Netzwerkfehler oder Gerät offline). */
export class ApiOffline extends Error {
  constructor() {
    super('Keine Verbindung zum Server');
    this.name = 'ApiOffline';
  }
}

/** Ein mutierender Aufruf ohne bekannte Schutzmarke: wird vor dem Senden abgelehnt. */
export class CsrfMissing extends Error {
  constructor() {
    super('Die Schutzmarke der Sitzung ist noch nicht bekannt');
    this.name = 'CsrfMissing';
  }
}

/**
 * Baut den Fehler aus einem Antwortrumpf. Ein kaputter oder fremder Rumpf (z. B. `502` eines Proxys) wird zu
 * einem synthetischen `internal`-Fehler mit `requestId = 'client'`.
 */
export function parseProblemBody(
  body: unknown,
  status: number,
  retryAfter: string | null,
): ApiProblem {
  const parsed = ProblemSchema.safeParse(body);
  if (!parsed.success) {
    return new ApiProblem({ code: 'internal', status, requestId: 'client' });
  }
  const { code, requestId, reason, detail, fieldErrors } = parsed.data;
  return new ApiProblem({
    code,
    status,
    requestId,
    reason,
    detail,
    fieldErrors,
    retryAfterSeconds: parseRetryAfter(retryAfter),
  });
}
